import React, { FC } from 'react';
import Dialog from 'carbon-react/lib/components/dialog';
import Form from 'carbon-react/lib/components/form';
import Typography from 'carbon-react/lib/components/typography';
import Button from 'carbon-react/lib/components/button';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UILoader } from '@futrli/ui-components';

import { useSheetSyncStatus } from '../../../hooks/use-sheets-sync-status/use-sheets-sync-status';
import styles from './sync-modal.scss';

export interface Props {
  forecastId: string;
  organisationId: string;
  workspaceId: string;
  onSync: () => void;
  onClose: () => void;
}

interface TemplateStatus {
  allowImport: boolean;
  incorrectSheetsToImport: string[];
  sheetsToImport: string[];
}

export const SyncModal: FC<Props> = ({ workspaceId, organisationId, forecastId, onSync, onClose }) => {
  const { result: templateStatus, isLoading } = useSheetSyncStatus(workspaceId, organisationId, forecastId);

  const intl = useIntl();

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      size="medium-small"
      data-testid="sync-dialog"
      aria-label={intl.formatMessage({ id: 'common.sync_status', defaultMessage: 'Sync status' })}
      title={intl.formatMessage({ id: 'common.sync_status', defaultMessage: 'Sync status' })}
    >
      <Form
        stickyFooter
        leftSideButtons={
          !isLoading && (
            <Button disabled={isLoading} onClick={onClose} data-testid="sync-dialog-actions-secondary">
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
          )
        }
        rightSideButtons={
          !isLoading &&
          templateStatus?.allowImport && (
            <Button
              disabled={!!templateStatus.error}
              onClick={onSync}
              buttonType="primary"
              data-testid="sync-dialog-actions-primary"
            >
              <FormattedMessage id="import.sync_predictions" defaultMessage="Sync Predictions" />
            </Button>
          )
        }
        onSubmit={e => e.preventDefault()}
      >
        <div data-testid="sync-dialog-content">
          {isLoading && <UILoader />}
          {templateStatus && !templateStatus.error && <DialogText templateStatus={templateStatus} />}
          {templateStatus?.error && (
            <Typography fontSize="16px" mb={0} data-testid="sync-dialog-error">
              <FormattedMessage
                id="import.sync_modal_error"
                defaultMessage="There has been an issue checking your master file. Please try again and contact support if it persists."
              />
            </Typography>
          )}
        </div>
      </Form>
    </Dialog>
  );
};

interface DialogTextProps {
  templateStatus: TemplateStatus;
}

const DialogText: FC<DialogTextProps> = ({ templateStatus }) => {
  // error text if sync is not able to proceed
  if (templateStatus.allowImport === false) {
    if (templateStatus.incorrectSheetsToImport.length) {
      return (
        <p data-testid="sync-dialog-text-incorrect-sheets">
          <FormattedMessage
            id="predict.import.sync_modal_incorrect_sheets"
            defaultMessage="Not all of the tab names provided match tabs in your master file.{br}{br}Please check your control sheet and try again."
            values={{
              br: <br />
            }}
          />
        </p>
      );
    }
    return (
      <p data-testid="sync-dialog-text-no-sheets">
        <FormattedMessage
          id="predict.import.sync_modal_no_sheets"
          defaultMessage="No sheets available to import. Please use the 'Control sheet' in your master file to tell us which tabs to pull into Predict."
          values={{
            br: <br />
          }}
        />
      </p>
    );
  }

  // sync is able to complete
  return (
    <React.Fragment>
      <p data-testid="sync-dialog-text-success">
        <FormattedMessage
          id="predict.import.sync_modal_sheets_to_import"
          defaultMessage="Data will be imported from these tabs in your master file:"
        />
      </p>
      <ul className={styles.list}>
        {templateStatus.sheetsToImport.map(sheetName => (
          <li key={sheetName}>{sheetName}</li>
        ))}
      </ul>
    </React.Fragment>
  );
};
